import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Select from 'react-select';
import PageHeader from 'components/common/PageHeader';
import FullPageSpinner from 'components/common/full-page-spinner';
import {
  getDepartments,
  getFinancialYears,
  getDepartmentSpendingSummary
} from 'services/department-spending/departmentSpendingService';
import DepartmentSpendingSummaryTable from './DepartmentSpendingSummaryTable';
import DepartmentSpendingTransactionsModal from './DepartmentSpendingTransactionsModal';
import { useDepartmentSpendingSelection } from './useDepartmentSpendingSelection';
import { sortSummaryPeriodsChronologically } from './summaryOrdering';

const DepartmentSpending = () => {
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [financialYearOptions, setFinancialYearOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);

  const [department, setDepartment] = useState(null);
  const [financialYear, setFinancialYear] = useState(null);

  const [summary, setSummary] = useState(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);

  const [selectedCell, setSelectedCell] = useState(null);

  useEffect(() => {
    const loadOptions = async () => {
      setIsLoadingOptions(true);
      try {
        const [departmentsRes, financialYearsRes] = await Promise.all([
          getDepartments(),
          getFinancialYears()
        ]);
        if (departmentsRes?.success === false)
          toast.error(departmentsRes.message);
        if (financialYearsRes?.success === false)
          toast.error(financialYearsRes.message);
        setDepartmentOptions(departmentsRes?.data || []);
        setFinancialYearOptions(financialYearsRes?.data || []);
      } catch (error) {
        console.log('AXIOS ERROR', error);
      } finally {
        setIsLoadingOptions(false);
      }
    };
    loadOptions();
  }, []);

  useLayoutEffect(() => {
    if (!department || !financialYear) {
      setSummary(null);
      return;
    }
    const loadSummary = async () => {
      setIsLoadingSummary(true);
      try {
        const res = await getDepartmentSpendingSummary(
          department.value,
          financialYear.value
        );
        if (res?.success === false) toast.error(res.message);
        setSummary(sortSummaryPeriodsChronologically(res?.data) || null);
      } catch (error) {
        console.log('AXIOS ERROR', error);
        setSummary(null);
      } finally {
        setIsLoadingSummary(false);
      }
    };
    loadSummary();
  }, [department, financialYear]);

  const departmentSelectOptions = useMemo(
    () =>
      departmentOptions.map(d => ({
        value: d.code,
        label: d.name
      })),
    [departmentOptions]
  );

  const financialYearSelectOptions = useMemo(
    () =>
      financialYearOptions.map(fy => ({
        value: fy.id,
        label: fy.label
      })),
    [financialYearOptions]
  );

  const { handleDepartmentChange, handleFinancialYearChange } =
    useDepartmentSpendingSelection({
      departmentSelectOptions,
      financialYearSelectOptions,
      department,
      setDepartment,
      financialYear,
      setFinancialYear
    });

  return (
    <>
      <PageHeader
        title="Department Spending"
        description="View Benefit / Budget / Variance spending by department and financial year."
        className="mb-3"
      />

      <Card>
        <Card.Header>
          <Row className="g-3">
            <Col xs={12} sm={6} md={4} lg={3}>
              <Form.Group controlId="department-spending-department">
                <Form.Label>Department</Form.Label>
                <Select
                  inputId="department-spending-department"
                  isClearable
                  isLoading={isLoadingOptions}
                  options={departmentSelectOptions}
                  value={department}
                  onChange={handleDepartmentChange}
                  placeholder="Select department..."
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </Form.Group>
            </Col>

            <Col xs={12} sm={6} md={4} lg={3}>
              <Form.Group controlId="department-spending-financial-year">
                <Form.Label>Financial Year</Form.Label>
                <Select
                  inputId="department-spending-financial-year"
                  isClearable
                  isLoading={isLoadingOptions}
                  options={financialYearSelectOptions}
                  value={financialYear}
                  onChange={handleFinancialYearChange}
                  placeholder="Select financial year..."
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Header>

        <Card.Body>
          <DepartmentSpendingSummaryTable
            summary={summary}
            onCellClick={setSelectedCell}
          />
        </Card.Body>
      </Card>

      <DepartmentSpendingTransactionsModal
        show={!!selectedCell}
        onClose={() => setSelectedCell(null)}
        department={department?.value}
        financialYearId={financialYear?.value}
        cell={selectedCell}
      />

      <FullPageSpinner
        show={isLoadingOptions || isLoadingSummary}
        quantity={3}
        variant="primary"
      />
    </>
  );
};

export default DepartmentSpending;
